import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Megaphone, Plus, Edit2, Trash2, Calendar, 
  AlertTriangle, Info, Bell, CheckCircle, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/20', icon: Info },
  normal: { label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: Bell },
  high: { label: 'High', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', icon: AlertTriangle },
  urgent: { label: 'Urgent', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: AlertTriangle }
};

const roleOptions = ['admin', 'sales', 'marketing', 'driver', 'vendor', 'manager', 'marketplace_vendor'];

const AnnouncementsPage = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal',
    target_roles: [],
    is_active: true,
    expires_at: ''
  });

  // Permission-based access
  const canCreateAnnouncements = hasPermission('announcements.create');

  useEffect(() => {
    loadAnnouncements();
  }, [canCreateAnnouncements]);

  const loadAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isAdmin ? '/announcements/all' : '/announcements';
      const res = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(res.data.announcements);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        expires_at: formData.expires_at || null
      };

      if (editingId) {
        await axios.put(`${API_BASE_URL}/announcements/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API_BASE_URL}/announcements`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement created');
      }

      setDialogOpen(false);
      resetForm();
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      priority: announcement.priority,
      target_roles: announcement.target_roles || [],
      is_active: announcement.is_active,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted');
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      message: '',
      priority: 'normal',
      target_roles: [],
      is_active: true,
      expires_at: ''
    });
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Announcements</h1>
        {canCreateAnnouncements && (
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Announcement
          </Button>
        )}
      </div>

      {announcements.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-50" />
          <p className="text-wt-text-muted text-lg">No announcements at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement, idx) => {
            const config = priorityConfig[announcement.priority] || priorityConfig.normal;
            const PriorityIcon = config.icon;

            return (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`${config.bg} border ${config.border} rounded-xl p-6`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-lg ${config.bg}`}>
                      <PriorityIcon className={`h-6 w-6 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-wt-text-primary">{announcement.title}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                          {config.label}
                        </span>
                        {!announcement.is_active && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/10 text-gray-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-wt-text-secondary whitespace-pre-wrap">{announcement.message}</p>
                      
                      <div className="flex items-center gap-4 mt-4 text-xs text-wt-text-muted">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(announcement.created_at).toLocaleDateString()}
                        </span>
                        {announcement.target_roles && announcement.target_roles.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {announcement.target_roles.join(', ')}
                          </span>
                        )}
                        {announcement.expires_at && (
                          <span className="flex items-center gap-1 text-orange-400">
                            Expires: {new Date(announcement.expires_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {canCreateAnnouncements && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(announcement)}
                        className="text-wt-text-muted hover:text-wt-text-primary"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(announcement.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-secondary">Title</Label>
              <Input
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="Announcement title"
              />
            </div>

            <div>
              <Label className="text-wt-text-secondary">Message</Label>
              <Textarea
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="Announcement content..."
                rows={4}
              />
            </div>

            <div>
              <Label className="text-wt-text-secondary">Priority</Label>
              <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {Object.entries(priorityConfig).map(([key, cfg]) => (
                    <SelectItem key={key} value={key} className={cfg.color}>
                      {cfg.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-wt-text-secondary mb-2 block">Target Roles (empty = all)</Label>
              <div className="flex flex-wrap gap-2">
                {roleOptions.map(role => (
                  <div
                    key={role}
                    onClick={() => toggleRole(role)}
                    className={`px-3 py-1.5 rounded-lg cursor-pointer transition-all ${
                      formData.target_roles.includes(role)
                        ? 'bg-wt-gold text-black'
                        : 'bg-wt-subtle text-wt-text-secondary hover:bg-wt-border'
                    }`}
                  >
                    {role === 'marketplace_vendor' ? 'Marketplace Vendors' : role}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-wt-text-secondary">Expires At (optional)</Label>
              <DateInput
                value={formData.expires_at}
                onChange={e => setFormData({...formData, expires_at: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-wt-text-secondary">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={v => setFormData({...formData, is_active: v})}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                {editingId ? 'Update' : 'Publish'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsPage;
